import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import CustomerForm from '../../components/sales/CustomerForm';
import ReceivePaymentModal from '../../components/sales/ReceivePaymentModal';
import api from '../../lib/api';

const Customers = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [paymentCustomer, setPaymentCustomer] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);
  const searchTimeout = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (!token || !storedUser) {
      navigate('/login');
      return;
    }

    setUser(JSON.parse(storedUser));
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);

      const response = await api.get(`/customers?${params}`);
      setCustomers(response.data.items ?? response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    setShowDropdown(false);

    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearch(value);
      if (value.trim()) setShowDropdown(true);
    }, 300);
  };

  const handleSelectCustomer = (customer) => {
    setSearchInput(customer.name);
    setSearch(customer.name);
    setShowDropdown(false);
  };

  const handleAddCustomer = () => {
    setSelectedCustomer(null);
    setShowForm(true);
  };

  const handleEditCustomer = (customer) => {
    setSelectedCustomer(customer);
    setShowForm(true);
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!confirm('Are you sure you want to delete this customer?')) return;

    try {
      await api.delete(`/customers/${customerId}`);
      fetchCustomers();
    } catch (error) {
      console.error('Error deleting customer:', error);
      alert('Failed to delete customer');
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setSelectedCustomer(null);
    fetchCustomers();
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const dropdownCustomers = customers.slice(0, 8);

  return (
    <div className="flex h-screen bg-[#f0f2f5]" data-testid="customers-page">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        <Navbar user={user} toggleSidebar={toggleSidebar} />

        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#1a1a2e]" data-testid="customers-title">
              Customers
            </h1>
            <button
              onClick={handleAddCustomer}
              className="bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-lg px-4 py-2 font-medium transition-colors flex items-center gap-2"
              data-testid="add-customer-button"
            >
              <Plus className="w-5 h-5" />
              Add Customer
            </button>
          </div>

          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm mb-6 p-4">
            <div className="relative" ref={searchRef}>
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#6b7280] pointer-events-none" />
              <input
                type="text"
                placeholder="Search by name, phone, or NTN..."
                className="w-full pl-10 pr-4 py-2 border border-[#e5e7eb] rounded-lg focus:ring-2 focus:ring-[#4A7AE0]/20 focus:border-[#4A7AE0]"
                value={searchInput}
                onChange={handleSearchChange}
                onFocus={() => { if (searchInput.trim() && customers.length > 0) setShowDropdown(true); }}
                data-testid="search-input"
              />
              {showDropdown && dropdownCustomers.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-[#e5e7eb] rounded-xl shadow-lg max-h-60 overflow-y-auto">
                  {dropdownCustomers.map((customer) => (
                    <button
                      key={customer.id}
                      onClick={() => handleSelectCustomer(customer)}
                      className="w-full flex items-center justify-between px-4 py-2.5 text-sm border-b border-[#e5e7eb] last:border-0 hover:bg-[#f9fafb] text-left"
                    >
                      <div>
                        <p className="font-medium text-[#1a1a2e]">{customer.name}</p>
                        <p className="text-xs text-[#9ca3af]">{customer.phone || 'No phone'} • Balance: Rs. {parseFloat(customer.balance || 0).toLocaleString()}</p>
                      </div>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-sm shrink-0 ml-3 ${customer.is_active ? 'bg-[#DBEAFE] text-[#1E40AF]' : 'bg-[#FEE2E2] text-[#991B1B]'}`}>
                        {customer.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">Loading...</div>
          ) : customers.length === 0 ? (
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-12 text-center">
              <p className="text-[#6b7280] mb-4">No customers found</p>
              <button onClick={handleAddCustomer} className="bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-lg px-4 py-2 font-medium transition-colors">
                Add Your First Customer
              </button>
            </div>
          ) : (
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm overflow-x-auto">
              <table className="w-full text-sm text-left min-w-[600px]" data-testid="customers-table">
                <thead className="bg-[#f9fafb] text-[#6b7280] font-medium border-b border-[#e5e7eb]">
                  <tr>
                    <th className="py-3 px-4">Name</th>
                    <th className="py-3 px-4">Phone</th>
                    <th className="py-3 px-4">Email</th>
                    <th className="py-3 px-4">Balance</th>
                    <th className="py-3 px-4">Outstanding</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((customer) => {
                    const outstanding = parseFloat(customer.outstanding_balance || 0);
                    const outstandingClass = customer.has_overdue ? 'text-[#EF4444] font-semibold' : 'text-[#1a1a2e]';
                    return (
                    <tr key={customer.id} className="border-b border-[#e5e7eb] hover:bg-[#f9fafb]">
                      <td className="py-3 px-4 font-normal text-[#1a1a2e]">{customer.name}</td>
                      <td className="py-3 px-4 font-normal text-[#1a1a2e]">{customer.phone || '-'}</td>
                      <td className="py-3 px-4 font-normal text-[#1a1a2e]">{customer.email || '-'}</td>
                      <td className="py-3 px-4 font-normal text-[#1a1a2e]">Rs. {parseFloat(customer.balance).toLocaleString()}</td>
                      <td className={`py-3 px-4 ${outstandingClass}`} data-testid={`outstanding-${customer.id}`}>
                        Rs. {outstanding.toLocaleString()}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-sm text-xs font-medium ${customer.is_active ? 'bg-[#DBEAFE] text-[#1E40AF]' : 'bg-[#FEE2E2] text-[#991B1B]'}`}>
                          {customer.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2 items-center flex-wrap">
                          {outstanding > 0 && (
                            <button
                              onClick={() => setPaymentCustomer(customer)}
                              className="bg-[#1a1a2e] text-white hover:bg-[#2d2d44] rounded-lg px-3 py-1.5 text-xs font-medium transition-colors"
                              data-testid={`receive-payment-${customer.id}`}
                            >
                              Receive Payment
                            </button>
                          )}
                          <button onClick={() => handleEditCustomer(customer)} className="inline-flex items-center px-3 py-1.5 rounded-lg border border-[#1E40AF] text-[#1E40AF] text-xs font-medium" data-testid={`edit-customer-${customer.id}`}>
                            Edit
                          </button>
                          <button onClick={() => handleDeleteCustomer(customer.id)} className="inline-flex items-center px-3 py-1.5 rounded-lg border border-[#EF4444] text-[#EF4444] text-xs font-medium" data-testid={`delete-customer-${customer.id}`}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>

      {showForm && (
        <CustomerForm customer={selectedCustomer} onClose={() => setShowForm(false)} onSuccess={handleFormSuccess} />
      )}

      {paymentCustomer && (
        <ReceivePaymentModal
          customer={paymentCustomer}
          onClose={() => setPaymentCustomer(null)}
          onSuccess={() => {
            const name = paymentCustomer.name;
            setPaymentCustomer(null);
            alert(`Payment recorded successfully for ${name}`);
            fetchCustomers();
          }}
        />
      )}
    </div>
  );
};

export default Customers;
